import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode.react";
import { useTranslation } from "react-i18next";
import { roadmakerApi } from "../../api/roadmakerApi";
import { shareLinkStore } from "../../api/shareLinkStore";
import { printQrCodeHTML } from "../util/PrintQrCodeHTML";

// Panneau « Partager » (ADR 0006) : gestion des liens de partage révocables
// d'un parcours, distinct du QR de téléchargement de l'Exporter.
// - le jeton brut est affiché À LA CRÉATION (QR + lien copiable) ; le serveur
//   ne stocke que son SHA-256 et ne peut pas le restituer ;
// - réaffichage (décision A, 2026-09-06) : le lien créé est mémorisé dans
//   localStorage par (projectId, shareId) → le QR du MÊME partage reste
//   réaffichable et imprimable à la réouverture de la modale, sur ce
//   navigateur uniquement (autre appareil = nouveau lien) ; la révocation
//   retire le lien mémorisé ;
// - chaque ligne expose libellé, dates, état dérivé, dernier accès ;
// - révocation explicite avec confirmation.
const LOGO_CONNEXION_IMG = "/rm_imgs/logo_connexion.png";

// Origine de la page servie : banc de test ou production, sans configuration.
const shareLinkFor = (token) =>
  `${window.location.origin}/share/${encodeURIComponent(token)}`;

const formatDate = (value, t) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const ShareRow = ({ share, onRevoke, onShowQr, hasStoredLink, t }) => {
  const state = !share.active
    ? share.revokedAt
      ? t("share.state.revoked")
      : t("share.state.expired")
    : t("share.state.active");
  const stateClass = share.active ? "text-success" : "text-muted";
  return (
    <tr data-testid={`share-row-${share.id}`}>
      <td>{share.label || t("share.unnamed")}</td>
      <td className={stateClass} data-testid={`share-state-${share.id}`}>
        {state}
      </td>
      <td>{formatDate(share.createdAt, t)}</td>
      <td>{formatDate(share.expiresAt, t)}</td>
      <td>{formatDate(share.lastSeenAt, t)}</td>
      <td>{share.accessCount ?? 0}</td>
      <td>
        <div className="d-flex gap-2 justify-content-end">
          {share.active && hasStoredLink && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              data-testid={`share-show-qr-${share.id}`}
              onClick={() => onShowQr(share)}
            >
              {t("share.showQr")}
            </button>
          )}
          {share.active && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              data-testid={`share-revoke-${share.id}`}
              onClick={() => {
                if (window.confirm(t("share.revokeConfirm"))) {
                  onRevoke(share.id);
                }
              }}
            >
              {t("share.revoke")}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const ShareProjectPanel = ({ projectId, onClose }) => {
  const { t } = useTranslation();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [label, setLabel] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null); // { share, link }
  // QR réaffiché depuis le store local : { share, link } | null.
  const [restored, setRestored] = useState(null);
  const qrCodeRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await roadmakerApi.shares.list(projectId);
      setShares(list);
    } catch (e) {
      // Clé i18n stockée (pas de t ici) : l'identité de t varie à chaque
      // rendu sous mock de test et sa dépendance bouclerait le rechargement.
      setError("share.loadError");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleCreate = async (event) => {
    event.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const payload = await roadmakerApi.shares.create(projectId, {
        label: label.trim(),
        expiresAt: expiresAt || null,
      });
      const link = shareLinkFor(payload.token);
      setCreated({
        share: payload.share,
        token: payload.token,
        link,
      });
      // Décision A : mémoriser pour réafficher le MÊME QR après fermeture.
      shareLinkStore.save(projectId, payload.share.id, link);
      setRestored(null);
      setLabel("");
      setExpiresAt("");
      await reload();
    } catch (e) {
      setError(t("share.createError"));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = useCallback(
    async (shareId) => {
      setError(null);
      try {
        await roadmakerApi.shares.revoke(projectId, shareId);
        // Le lien mémorisé ne doit plus être réaffichable une fois révoqué.
        shareLinkStore.remove(projectId, shareId);
        if (restored?.share?.id === shareId) setRestored(null);
        if (created?.share?.id === shareId) setCreated(null);
        await reload();
      } catch (e) {
        setError(t("share.revokeError"));
      }
    },
    [projectId, reload, restored, created]
  );

  // Réaffiche le QR d'un partage actif dont le lien est mémorisé localement.
  const handleShowQr = useCallback(
    (share) => {
      const link = shareLinkStore.load(projectId, share.id);
      if (!link) return;
      setCreated(null);
      setRestored({ share, link });
    },
    [projectId]
  );

  const copyLink = async () => {
    if (!created?.link) return;
    try {
      await navigator.clipboard.writeText(created.link);
    } catch (_) {
      // Presse-papier indisponible : le lien reste sélectionnable à l'écran.
    }
  };

  // Impression du QR affiché (même mécanique que ConfigTouristicMarker).
  const handlePrint = (label) => {
    const qrCodeElement = qrCodeRef.current;
    if (!qrCodeElement) return;
    const canvas = qrCodeElement.querySelector("canvas");
    if (!canvas) return;
    const imgData = canvas.toDataURL();
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      console.error(
        "Failed to open print window. Check if pop-ups are blocked."
      );
      return;
    }
    printWindow.document.write(printQrCodeHTML(LOGO_CONNEXION_IMG, imgData, { title: label }));
    printWindow.document.close();
    printWindow.onload = function () {
      printWindow.print();
    };
  };

  // Lien mémorisé connu pour chaque partage (réaffichage QR possible).
  const storedLinkByShareId = useMemo(() => {
    const map = {};
    for (const share of shares) {
      if (share.active && shareLinkStore.load(projectId, share.id)) {
        map[share.id] = true;
      }
    }
    return map;
  }, [shares, projectId]);

  const activeCount = useMemo(
    () => shares.filter((share) => share.active).length,
    [shares]
  );

  if (!projectId) return null;

  return (
    <div
      className="modal d-block"
      style={{ background: "rgba(0,0,0,0.5)" }}
      data-testid="share-panel"
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("share.title")}</h5>
            <button
              type="button"
              className="btn-close"
              data-testid="share-close"
              aria-label={t("share.close")}
              onClick={onClose}
            />
          </div>
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger" data-testid="share-error">
                {error === "share.loadError" ? t(error) : error}
              </div>
            )}

            {created && (
              <div className="alert alert-success" data-testid="share-created">
                <p className="mb-2">
                  <strong>{t("share.createdOnce")}</strong>
                </p>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div ref={qrCodeRef} data-testid="share-qr-wrapper">
                    <QRCode value={created.link} size={148} includeMargin />
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <code
                      className="d-block text-break small"
                      data-testid="share-created-link"
                    >
                      {created.link}
                    </code>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary mt-2"
                      onClick={copyLink}
                    >
                      {t("share.copyLink")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary mt-2 ms-2"
                      data-testid="share-print"
                      onClick={() => handlePrint(created.share?.label)}
                    >
                      {t("share.printQr")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {restored && (
              <div className="alert alert-info" data-testid="share-restored">
                <p className="mb-2">
                  <strong>{t("share.restoredTitle")}</strong>{" "}
                  <span>{restored.share.label || t("share.unnamed")}</span>
                </p>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div ref={qrCodeRef} data-testid="share-qr-wrapper">
                    <QRCode value={restored.link} size={148} includeMargin />
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <code
                      className="d-block text-break small"
                      data-testid="share-restored-link"
                    >
                      {restored.link}
                    </code>
                    <p className="small text-muted mb-2 mt-2">
                      {t("share.restoredScope")}
                    </p>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      data-testid="share-print"
                      onClick={() => handlePrint(restored.share.label)}
                    >
                      {t("share.printQr")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <form
              className="row g-2 align-items-end mb-3"
              onSubmit={handleCreate}
              data-testid="share-create-form"
            >
              <div className="col-md-5">
                <label className="form-label" htmlFor="share-label">
                  {t("share.label")}
                </label>
                <input
                  id="share-label"
                  type="text"
                  className="form-control"
                  maxLength={120}
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                  placeholder={t("share.labelPlaceholder")}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label" htmlFor="share-expires">
                  {t("share.expiresAt")}
                </label>
                <input
                  id="share-expires"
                  type="datetime-local"
                  className="form-control"
                  value={expiresAt}
                  onChange={(event) => setExpiresAt(event.target.value)}
                />
              </div>
              <div className="col-md-3">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={creating}
                  data-testid="share-create"
                >
                  {creating ? t("share.creating") : t("share.create")}
                </button>
              </div>
            </form>

            {loading ? (
              <p data-testid="share-loading">{t("share.loading")}</p>
            ) : (
              <>
                <p className="text-muted small">
                  {t("share.activeCount", { count: activeCount })}
                </p>
                {shares.length === 0 ? (
                  <p data-testid="share-empty">{t("share.empty")}</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>{t("share.col.label")}</th>
                          <th>{t("share.col.state")}</th>
                          <th>{t("share.col.createdAt")}</th>
                          <th>{t("share.col.expiresAt")}</th>
                          <th>{t("share.col.lastSeen")}</th>
                          <th>{t("share.col.accessCount")}</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {shares.map((share) => (
                          <ShareRow
                            key={share.id}
                            share={share}
                            onRevoke={handleRevoke}
                            onShowQr={handleShowQr}
                            hasStoredLink={Boolean(storedLinkByShareId[share.id])}
                            t={t}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareProjectPanel;
